package com.nonopn.skywatcher.common

import com.nonopn.skywatcher.app.utils.Queue
import com.nonopn.skywatcher.app.utils.RootPath
import com.nonopn.skywatcher.app.utils.readStringIfExists
import com.nonopn.skywatcher.app.utils.takeIf
import com.nonopn.skywatcher.common.api.APIUserSession
import com.nonopn.skywatcher.common.api.ApiNoSession
import com.nonopn.skywatcher.common.api.ApiProvider
import com.nonopn.skywatcher.common.api.ApiSession
import com.nonopn.skywatcher.common.api.ChangePasswordRequest
import com.nonopn.skywatcher.common.api.CheckoutLoginRequest
import com.nonopn.skywatcher.common.api.LoginRequest
import com.nonopn.skywatcher.common.api.SavedSession
import com.nonopn.skywatcher.common.api.isAdministrator
import eu.codlab.files.VirtualFile
import eu.codlab.http.Configuration
import eu.codlab.http.createClient
import io.ktor.client.HttpClient
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.plugins.auth.providers.bearer
import io.ktor.utils.io.core.toByteArray
import korlibs.io.async.CIO
import korlibs.time.DateTime
import korlibs.time.milliseconds
import korlibs.time.months
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.serialization.KSerializer
import kotlinx.serialization.json.Json

private var baseUrl = "https://api.lpsmanager.io"
const val Millis = 1000L
private const val PersistenceTokenDuration = 60L * 60 * 24 * 30 * 6

class SessionManager<T, D>(
    folder: String,
    private val serializer: KSerializer<D>,
    val admin: (HttpClient) -> T,
    val normal: (HttpClient) -> T
) {
    private val queue = Queue(CoroutineScope(Dispatchers.CIO))
    private val rootDir = VirtualFile(RootPath, "NonOpn/$folder")
    private val sessionFile = VirtualFile(rootDir, "user.json")
    private var savedSession: SavedSession<D>? = null

    fun getFile(fileName: String) = VirtualFile(rootDir, fileName)

    private val apiNoSession = ApiNoSession(baseUrl, createClient { })

    suspend fun getSpecificData() = readSessionFile()?.specificData

    @Suppress("TooGenericExceptionCaught")
    private suspend fun readSessionFile(): SavedSession<D>? = try {
        if (null == savedSession) {
            savedSession = sessionFile.readStringIfExists()?.let {
                Json.decodeFromString(SavedSession.serializer(serializer), it)
            }
        }

        savedSession
    } catch (_: Throwable) {
        null
    }

    suspend fun initialize(): ApiProvider<T>? {
        if (!rootDir.exists()) {
            rootDir.mkdirs()
        }

        val session = readSessionFile()

        return try {
            val apiProvider = generateClient(session?.token, false)

            val userFromBackend = apiProvider!!.apiSession.profile()

            saveTokenFromBackend(userFromBackend.persistenceToken, PersistenceTokenDuration)

            generateClient(userFromBackend.persistenceToken, userFromBackend.isAdministrator())
        } catch (_: Throwable) {
            null
        }
    }

    private suspend fun saveTokenFromBackend(token: String? = null, expiresIn: Long) {
        val now = DateTime.now().add(0.months, (expiresIn * Millis).milliseconds)
        val specificData = readSessionFile()?.specificData
        SavedSession(
            token = token,
            expiresAt = now.unixMillisLong,
            specificData = specificData
        ).let {
            savedSession = it
            Json.encodeToString(
                SavedSession.serializer(
                    serializer
                ),
                it
            )
        }.let {
            queue.enqueue {
                sessionFile.write(it.toByteArray())
            }
        }
    }

    suspend fun saveSpecificData(specificData: D) {
        (readSessionFile() ?: SavedSession(expiresAt = 0L))
            .copy(specificData = specificData)
            .let {
                savedSession = it

                queue.enqueue {
                    sessionFile.write(
                        Json.encodeToString(
                            SavedSession.serializer(
                                serializer
                            ),
                            it
                        ).toByteArray()
                    )
                }
            }
    }

    suspend fun register(email: String, password: String) =
        callRegister { apiNoSession.register(LoginRequest(email, password)) }

    suspend fun login(email: String, password: String) =
        callRegister { apiNoSession.login(LoginRequest(email, password)) }

    /**
     * Return from the Stripe Checkout: exchanges the one time code for the token of the account which paid
     * (POST /api/checkout/login), then opens the session like a login. Throws on any error (InvalidLoginCode…).
     */
    suspend fun loginWithCheckoutCode(request: CheckoutLoginRequest): ApiProvider<T> =
        loginWithToken(apiNoSession.checkoutLogin(request).token)

    /**
     * Opens a session from a bearer token alone (no login answer): GET /user checks the token and gives the
     * persistence token and the administrator right, then the token is saved like after a login.
     */
    suspend fun loginWithToken(token: String): ApiProvider<T> {
        if (!rootDir.exists()) {
            rootDir.mkdirs()
        }

        val profile = generateClient(token, false)!!.apiSession.profile()
        val persistenceToken = profile.persistenceToken.ifBlank { token }

        saveTokenFromBackend(persistenceToken, PersistenceTokenDuration)

        return generateClient(persistenceToken, profile.isAdministrator())!!
    }

    // The backend returns a new token after a password change: persist it and rebuild the clients
    suspend fun changePassword(
        apiSession: ApiSession,
        request: ChangePasswordRequest,
        generateWithAdmin: Boolean
    ): ApiProvider<T> {
        val answer = apiSession.changePassword(request)

        saveTokenFromBackend(answer.token, PersistenceTokenDuration)

        return generateClient(answer.token, generateWithAdmin)!!
    }

    private suspend fun callRegister(call: suspend () -> APIUserSession): ApiProvider<T> {
        val session = call()

        saveTokenFromBackend(session.persistenceToken, PersistenceTokenDuration) // 6 months

        if (!sessionFile.exists()) {
            throw IllegalStateException(
                "unable to find the file for the session " +
                        sessionFile.absolutePath
            )
        }

        return generateClient(session.persistenceToken, hasAdministratorRight(session))!!
    }

    // Admin rule: GET /user `rights` contains "administrator". Read with the new token after login/register;
    // if /user fails, the rights of the login answer are used (never the `admin` field).
    @Suppress("TooGenericExceptionCaught")
    private suspend fun hasAdministratorRight(session: APIUserSession): Boolean = try {
        generateClient(session.persistenceToken, false)!!.apiSession.profile().isAdministrator()
    } catch (e: CancellationException) {
        throw e
    } catch (_: Throwable) {
        session.isAdministrator()
    }

    private fun generateClient(
        token: String? = null,
        generateWithAdmin: Boolean?
    ): ApiProvider<T>? {
        if (null == token) {
            return null
        }

        val wrapper = createClient(
            configuration = Configuration(
                enableSocket = true,
                // Never log this client: the HTTP logs (LogLevel.ALL in eu.codlab:kotlin-http-client) print the
                // Authorization bearer header and the response bodies holding the session token (profile, login)
                enableLogs = false,
                connectTimeoutMillis = 30000,
                requestTimeoutMillis = 30000,
                socketTimeoutMillis = 30000
            ),
            onAuth = {
                bearer {
                    loadTokens {
                        BearerTokens(token, "")
                    }
                }
            }
        )

        return ApiProvider(
            apiAdmin = takeIf(generateWithAdmin == true) {
                admin(wrapper)
            },
            apiNormal = normal(wrapper),
            apiSession = ApiSession(wrapper)
        )
    }

    suspend fun logout() = saveTokenFromBackend(null, 0)
}
